import { useState } from 'react'
import { DOCS_BASE } from '../client-guides'
import { AgentTokens } from './AgentTokens'
import { Icon } from './Icon'

const MCP_URL = 'https://mcp.staleless.com/mcp'

const CLI_INSTALL = 'npm install --save-dev staleless'
const CLI_CHECKS = `npx staleless lint docs
npx staleless audit --content docs
STALELESS_TOKEN=... npx staleless check`

const CODEX_SETUP = `codex mcp add staleless --url ${MCP_URL} \\
  --bearer-token-env-var STALELESS_MCP_TOKEN`

const MCP_CLIENT_SETUP = `{
  "mcpServers": {
    "staleless": {
      "url": "${MCP_URL}"
    }
  }
}`

type McpClient = 'codex' | 'other'

function shellQuote(value: string): string {
  return `'${value.replaceAll("'", `'"'"'`)}'`
}

function codexSetup(token: string): string {
  return `export STALELESS_TOKEN=${shellQuote(token)}
codex mcp add staleless --url ${MCP_URL} --bearer-token-env-var STALELESS_TOKEN`
}

function mcpConfig(token: string): string {
  return `{
  "mcpServers": {
    "staleless": {
      "url": "${MCP_URL}",
      "headers": {
        "Authorization": "Bearer ${token}"
      }
    }
  }
}`
}

function Snippet({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1600)
  }
  return (
    <div className="developer__snippet">
      <div className="developer__snippet-head">
        <span>{label}</span>
        <button className="btn btn--ghost btn--sm" onClick={() => void copy()}>
          <Icon name={copied ? 'check' : 'copy'} size={13} />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre>
        <code>{value}</code>
      </pre>
    </div>
  )
}

export function DevelopersScreen({ mayManage }: { mayManage: boolean }) {
  const [tool, setTool] = useState<'mcp' | 'cli'>('mcp')
  const [mcpClient, setMcpClient] = useState<McpClient>('codex')

  const setup = (token: string) => (mcpClient === 'codex' ? codexSetup(token) : mcpConfig(token))

  return (
    <div className="screen screen--developers">
      <header className="screen__head screen__head--split">
        <div>
          <h1>Developers</h1>
          <p className="screen__sub">
            Connect agents through MCP, or keep documentation checks beside the code with the CLI.
          </p>
        </div>
        <a
          className="btn btn--ghost btn--sm"
          href={`${DOCS_BASE}/developers/`}
          target="_blank"
          rel="noreferrer"
        >
          Developer docs <Icon name="chevron-right" size={13} />
        </a>
      </header>

      <div className="developer__switcher" role="tablist" aria-label="Developer tool">
        <button
          role="tab"
          aria-selected={tool === 'mcp'}
          className={tool === 'mcp' ? 'is-on' : ''}
          onClick={() => setTool('mcp')}
        >
          <Icon name="blocks" size={16} />
          <span>
            <strong>MCP</strong>
            <small>AI agents</small>
          </span>
        </button>
        <button
          role="tab"
          aria-selected={tool === 'cli'}
          className={tool === 'cli' ? 'is-on' : ''}
          onClick={() => setTool('cli')}
        >
          <Icon name="code" size={16} />
          <span>
            <strong>CLI</strong>
            <small>Local and CI</small>
          </span>
        </button>
      </div>

      {tool === 'mcp' ? (
        <section className="developer__body" aria-label="MCP setup">
          <div className="developer__intro">
            <span className="badge badge--accent">MCP</span>
            <div>
              <h2>Let an agent work with the documentation</h2>
              <p>
                The token selects this documentation site. Scopes decide whether the agent can only
                read, stage edits, or commit a review branch. MCP can never publish.
              </p>
            </div>
          </div>
          <ol className="developer__steps">
            <li>
              <span>1</span>
              <div>
                <strong>Choose your client</strong>
                <p>Start with Codex or use the standard configuration for another client.</p>
              </div>
            </li>
            <li>
              <span>2</span>
              <div>
                <strong>Mint a token</strong>
                <p>Staleless puts the one-time token into the generated setup.</p>
              </div>
            </li>
            <li>
              <span>3</span>
              <div>
                <strong>Connect and review</strong>
                <p>
                  Agent changes arrive as a draft and stay away from readers until a person
                  publishes.
                </p>
              </div>
            </li>
          </ol>
          <div className="developer__client">
            <div>
              <h2>Connect a client</h2>
              <p>Choose where the agent runs, then mint a token for that connection.</p>
            </div>
            <div className="developer__client-tabs" role="tablist" aria-label="MCP client">
              <button
                role="tab"
                aria-selected={mcpClient === 'codex'}
                className={mcpClient === 'codex' ? 'is-on' : ''}
                onClick={() => setMcpClient('codex')}
              >
                Codex
              </button>
              <button
                role="tab"
                aria-selected={mcpClient === 'other'}
                className={mcpClient === 'other' ? 'is-on' : ''}
                onClick={() => setMcpClient('other')}
              >
                Other MCP client
              </button>
            </div>
          </div>
          <div className="developer__facts">
            <span>
              <small>Endpoint</small>
              <code>{MCP_URL}</code>
            </span>
            <span>
              <small>Authentication</small>
              <code>Bearer STALELESS_TOKEN</code>
            </span>
          </div>
          <section className="developer__quickstart">
            <div>
              <h2>Connection commands</h2>
              <p>
                Add the server first, then create a scoped token below. The token is never included
                in these commands or stored in this page.
              </p>
            </div>
            <Snippet label="Codex" value={CODEX_SETUP} />
            <Snippet label="Other MCP clients" value={MCP_CLIENT_SETUP} />
          </section>
          <section className="developer__tokens">
            <div>
              <h2>Access tokens</h2>
              <p>
                Each token belongs to this documentation site, expires, and can be revoked here.
              </p>
            </div>
            <AgentTokens
              mayManage={mayManage}
              renderMinted={(token, dismiss) => (
                <div className="tokens__new developer__ready">
                  <div>
                    <strong>
                      {mcpClient === 'codex'
                        ? 'Your Codex connection is ready'
                        : 'Your MCP connection is ready'}
                    </strong>
                    <p>
                      This setup contains the token. Copy it now; Staleless keeps only its hash and
                      cannot show it again.
                    </p>
                  </div>
                  <Snippet
                    label={mcpClient === 'codex' ? 'Run in a terminal' : 'Add to your MCP client'}
                    value={setup(token)}
                  />
                  {mcpClient === 'codex' ? (
                    <p className="developer__note">
                      Run both lines in the terminal that starts Codex, then open a new Codex
                      session. Use <code>/mcp</code> to confirm that Staleless is active.
                    </p>
                  ) : null}
                  <div className="screen__actions">
                    <button className="btn btn--sm" onClick={dismiss}>
                      Done
                    </button>
                  </div>
                </div>
              )}
            />
          </section>
        </section>
      ) : (
        <section className="developer__body" aria-label="CLI setup">
          <div className="developer__intro">
            <span className="badge badge--accent">CLI</span>
            <div>
              <h2>Check documentation where the code changes</h2>
              <p>
                Lint Markdown without a network request, audit a documentation tree, and use a
                scoped token only for checks that compare against the live workspace.
              </p>
            </div>
          </div>
          <Snippet label="Install" value={CLI_INSTALL} />
          <Snippet label="Common checks" value={CLI_CHECKS} />
          <div className="developer__command-list">
            <div>
              <code>staleless lint</code>
              <span>Formatting, metadata and broken internal links.</span>
            </div>
            <div>
              <code>staleless audit</code>
              <span>Navigation, orphan pages and documentation structure.</span>
            </div>
            <div>
              <code>staleless impact</code>
              <span>Pages affected by files changed in a pull request.</span>
            </div>
            <div>
              <code>staleless check</code>
              <span>Compare a checkout with the connected Staleless site.</span>
            </div>
          </div>
          <a
            className="btn btn--tinted btn--sm"
            href={`${DOCS_BASE}/developers/cli/`}
            target="_blank"
            rel="noreferrer"
          >
            CLI reference <Icon name="chevron-right" size={13} />
          </a>
        </section>
      )}
    </div>
  )
}
